import React from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";
import {
  getClientErrors,
  getClientErrorUsers,
} from "../../redux/dynamo_db/actions";
import { MaterialSymbol } from "@datalake/towercontrol-banner";

// Admin-only page listing the front-end blocking issues reported by
// `reportClientError` (network timeouts, CORS/network failures, 5xx). Every
// entry carries the full diagnostic context we could gather from the browser.
export default function ClientErrors() {
  const dispatch = useDispatch();
  const stored = useSelector((state) => state.clientErrors) || [];
  const users = useSelector((state) => state.clientErrorUsers) || [];
  // Reports a user downloaded and sent (never reached the API) can be imported
  // here to be viewed alongside the stored ones. `imported: true` flags them.
  const [imported, setImported] = React.useState([]);
  const [selectedUser, setSelectedUser] = React.useState("");
  const [selectedDate, setSelectedDate] = React.useState("");
  const clientErrors = [...imported, ...stored];

  const refresh = React.useCallback(() => {
    dispatch(
      getClientErrors({
        requestUser: selectedUser || undefined,
        date: selectedDate || undefined,
        limit: 200,
      }),
    );
  }, [dispatch, selectedUser, selectedDate]);

  // Refetch whenever the user/date filter changes.
  React.useEffect(() => {
    refresh();
  }, [refresh]);

  // Load the list of users with errors once (for the dropdown).
  React.useEffect(() => {
    dispatch(getClientErrorUsers());
  }, [dispatch]);

  const importReport = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(reader.result);
        const entries = [
          ...(parsed.current ? [parsed.current] : []),
          ...(Array.isArray(parsed.pending) ? parsed.pending : []),
          ...(Array.isArray(parsed) ? parsed : []),
        ].map((e) => ({ ...e, imported: true }));
        setImported((prev) => [...entries, ...prev]);
      } catch {
        // eslint-disable-next-line no-alert
        alert("Invalid report file: expected the downloaded JSON.");
      }
    };
    reader.readAsText(file);
    event.target.value = "";
  };

  const severityColor = (type) => {
    if (type === "timeout") return "warning";
    if (type === "network") return "error";
    return "default";
  };

  return (
    <Box sx={{ p: 2, textAlign: "left", width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 2,
          flexWrap: "wrap",
        }}
      >
        <Typography
          variant="h6"
          sx={{ flexGrow: 1, color: "var(--ale-primary)" }}
        >
          Front-end blocking issues ({clientErrors.length})
        </Typography>

        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="ce-user-label">User</InputLabel>
          <Select
            labelId="ce-user-label"
            label="User"
            value={selectedUser}
            onChange={(e) => setSelectedUser(e.target.value)}
          >
            <MenuItem value="">
              <em>All users</em>
            </MenuItem>
            {users.map((u) => (
              <MenuItem key={u.request_user} value={u.request_user}>
                {u.request_user}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          size="small"
          label="Day"
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />

        <Button
          variant="outlined"
          startIcon={<MaterialSymbol name="upload_file" />}
          component="label"
        >
          Import report
          <input
            type="file"
            accept="application/json,.json"
            hidden
            onChange={importReport}
          />
        </Button>
        <Button
          variant="outlined"
          startIcon={<MaterialSymbol name="refresh" />}
          onClick={refresh}
        >
          Refresh
        </Button>
      </Box>

      {clientErrors.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No reported front-end issues.
        </Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Time (UTC)</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Resource</TableCell>
                <TableCell>HTTP</TableCell>
                <TableCell>Page</TableCell>
                <TableCell>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {clientErrors.map((e, i) => (
                <TableRow key={e.SK || i} hover>
                  <TableCell>{e.timestamp}</TableCell>
                  <TableCell>
                    {e.request_user || e.username || "—"}
                    {e.imported && (
                      <Chip
                        size="small"
                        label="imported"
                        variant="outlined"
                        sx={{ ml: 0.5 }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={e.errorType || "—"}
                      color={severityColor(e.errorType)}
                    />
                  </TableCell>
                  <TableCell>{e.resource || "—"}</TableCell>
                  <TableCell>{e.responseCode ?? "—"}</TableCell>
                  <TableCell>{e.page || "—"}</TableCell>
                  <TableCell sx={{ maxWidth: 360 }}>
                    <Accordion disableGutters elevation={0}>
                      <AccordionSummary
                        expandIcon={<MaterialSymbol name="expand_more" />}
                      >
                        <Typography variant="caption">
                          {e.durationMs != null ? `${e.durationMs} ms` : "view"}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <pre
                          style={{
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-all",
                            fontSize: "0.72rem",
                            margin: 0,
                          }}
                        >
                          {JSON.stringify(e, null, 2)}
                        </pre>
                      </AccordionDetails>
                    </Accordion>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
